import * as TabsPrimitive from '@radix-ui/react-tabs';
import * as React from 'react';

import { cn } from '~/utils/cn';

const Tabs = TabsPrimitive.Root;

/**
 * TabsList renders Radix's list with a single absolutely-positioned pill that
 * slides to the active trigger. The pill is measured from the trigger marked
 * `data-state="active"` and animated via CSS `transition-all`, so consumers
 * get the slide for free without local state or per-page wrappers.
 */
function TabsList({
  className,
  children,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> & {
  ref?: React.Ref<React.ComponentRef<typeof TabsPrimitive.List>>;
}) {
  const internalRef = React.useRef<HTMLDivElement>(null);
  const [indicator, setIndicator] = React.useState<{ left: number; width: number } | null>(null);

  /** Measure the active trigger and re-run on data-state mutations + container resizes. */
  React.useLayoutEffect(() => {
    const list = internalRef.current;
    if (!list) return;

    function measure() {
      const active = list?.querySelector<HTMLElement>('[data-state="active"]');
      if (!active) return;
      setIndicator({ left: active.offsetLeft, width: active.offsetWidth });
    }

    measure();

    const mo = new MutationObserver(measure);
    mo.observe(list, {
      attributes: true,
      attributeFilter: ['data-state'],
      subtree: true,
      childList: true,
    });

    const ro = new ResizeObserver(measure);
    ro.observe(list);

    return () => {
      mo.disconnect();
      ro.disconnect();
    };
  }, []);

  /** Forwards the DOM node to both the internal ref (for measurement) and the caller's ref. */
  function setRefs(node: HTMLDivElement | null) {
    internalRef.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) ref.current = node;
  }

  return (
    <TabsPrimitive.List
      ref={setRefs}
      className={cn(
        'text-muted-foreground tab-border relative inline-flex h-auto items-center justify-center rounded-lg border-2 p-1',
        className,
      )}
      {...props}
    >
      {indicator && (
        <span
          aria-hidden="true"
          className="absolute top-1 bottom-1 rounded-lg bg-[#445CE4] shadow-sm transition-all duration-300 ease-out"
          style={{ left: indicator.left, width: indicator.width }}
        />
      )}
      {children}
    </TabsPrimitive.List>
  );
}

/**
 * TabsTrigger sits above the shared sliding pill (`relative z-10`); the pill
 * owns the active background, so the trigger only flips its text colour.
 */
function TabsTrigger({
  className,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> & {
  ref?: React.Ref<React.ComponentRef<typeof TabsPrimitive.Trigger>>;
}) {
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        'text-muted-foreground ring-offset-background focus-visible:ring-ring relative z-10 inline-flex cursor-pointer items-center justify-center gap-1.5 rounded-lg px-10 py-2 text-sm font-medium whitespace-nowrap transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 data-[state=active]:text-white',
        className,
      )}
      {...props}
    />
  );
}

function TabsContent({
  className,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content> & {
  ref?: React.Ref<React.ComponentRef<typeof TabsPrimitive.Content>>;
}) {
  return (
    <TabsPrimitive.Content
      ref={ref}
      className={cn(
        'ring-offset-background focus-visible:ring-ring mt-2 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
        className,
      )}
      {...props}
    />
  );
}

export { Tabs, TabsList, TabsTrigger, TabsContent };
